import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Stack, TextField, Alert, Box, Divider, Typography, Link } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import LoaderButton from "../components/LoaderButton";
import FormPage from "../components/FormPage";

const requestSchema = Yup.object({
  Email: Yup.string()
    .required("Email is required")
    .matches(
      /^[a-zA-Z0-9.!#$%&’'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
      "Invalid email address"
    )
});

const confirmSchema = Yup.object({
  Email: Yup.string().required("Email is required"),
  Code: Yup.string().required("Code is required"),
  NewPassword: Yup.string()
    .required("New password required")
    .min(8, "Password too short — minimum 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      "Must contain lowercase, uppercase, number, and special character."
    ),
  ConfirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("NewPassword")], "Passwords must match")
});

export default function ResetPassword() {
  const [stage, setStage] = useState("request"); // "request" | "confirm" | "done"
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  if (stage === "done") {
    return (
      <FormPage title="Password reset" subtitle="Your password has been reset.">
        <Typography variant="body2">
          <Link component={RouterLink} to="/login">Sign in with your new password →</Link>
        </Typography>
      </FormPage>
    );
  }

  const handleRequest = async (event) => {
    setIsLoading(true);
    setAlertMessage("");
    setInfoMessage("");
    try {
      await resetPassword({ username: event.Email });
      setEmail(event.Email);
      setStage("confirm");
      setInfoMessage("Reset code sent to your email.");
    } catch (e) {
      setAlertMessage(e?.message ?? "Request failed");
    }
    setIsLoading(false);
  };

  const handleConfirm = async (event) => {
    setIsLoading(true);
    setAlertMessage("");
    try {
      await confirmResetPassword({
        username: event.Email,
        confirmationCode: event.Code,
        newPassword: event.NewPassword
      });
      setStage("done");
    } catch (e) {
      setAlertMessage(e?.message ?? "Reset failed");
    }
    setIsLoading(false);
  };

  return (
    <FormPage
      title="Reset your password"
      subtitle={
        stage === "request"
          ? "Enter your email and we'll send you a reset code."
          : "Enter the code from your email and choose a new password."
      }
    >
      {alertMessage ? (
        <Alert severity="error" onClose={() => setAlertMessage("")}>{alertMessage}</Alert>
      ) : null}
      {infoMessage ? (
        <Alert severity="info" onClose={() => setInfoMessage("")}>{infoMessage}</Alert>
      ) : null}

      {stage === "request" ? (
        <Formik
          validateOnChange={false}
          validationSchema={requestSchema}
          onSubmit={handleRequest}
          initialValues={{ Email: "" }}
        >
          {({ handleSubmit, handleChange, values, errors }) => (
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  name="Email"
                  label="Email"
                  type="email"
                  value={values.Email}
                  onChange={handleChange}
                  error={!!errors.Email}
                  helperText={errors.Email}
                  autoFocus
                />
                <Stack direction="row" spacing={1.5}>
                  <LoaderButton
                    type="submit"
                    isLoading={isLoading}
                    text="Send code"
                    loadingText="Sending…"
                    fullWidth
                  />
                  <LoaderButton
                    type="button"
                    onClick={() => {
                      setStage("confirm");
                      setEmail("");
                    }}
                    variant="outlined"
                    text="I have a code"
                    fullWidth
                  />
                </Stack>
              </Stack>
            </Box>
          )}
        </Formik>
      ) : (
        <Formik
          validateOnChange={false}
          validationSchema={confirmSchema}
          onSubmit={handleConfirm}
          initialValues={{ Email: email, Code: "", NewPassword: "", ConfirmPassword: "" }}
          enableReinitialize
        >
          {({ handleSubmit, handleChange, values, errors }) => (
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  name="Email"
                  label="Email"
                  type="email"
                  value={values.Email}
                  onChange={handleChange}
                  error={!!errors.Email}
                  helperText={errors.Email}
                  autoComplete="email"
                />
                <TextField
                  name="Code"
                  label="Confirmation code"
                  value={values.Code}
                  onChange={handleChange}
                  error={!!errors.Code}
                  helperText={errors.Code}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                  autoFocus
                />
                <Divider />
                <TextField
                  name="NewPassword"
                  label="New password"
                  type="password"
                  value={values.NewPassword}
                  onChange={handleChange}
                  error={!!errors.NewPassword}
                  helperText={errors.NewPassword}
                  autoComplete="new-password"
                />
                <TextField
                  name="ConfirmPassword"
                  label="Confirm new password"
                  type="password"
                  value={values.ConfirmPassword}
                  onChange={handleChange}
                  error={!!errors.ConfirmPassword}
                  helperText={errors.ConfirmPassword}
                  autoComplete="new-password"
                />
                <LoaderButton
                  type="submit"
                  isLoading={isLoading}
                  text="Reset password"
                  loadingText="Resetting…"
                  fullWidth
                  size="large"
                />
              </Stack>
            </Box>
          )}
        </Formik>
      )}
    </FormPage>
  );
}
